import { ProcedureDocumentType, ProcedureStatus } from '@caresend/types';
import { deduplicateArray, nullishFilter, hasStatus, formatPhoneNumber, getServiceRegionFromLocation, isNullish } from '@caresend/utils';
import { usePortalStore } from '../../components/specific/portal/store/portal/index.js';
import 'file-saver';
import '../../database/firebaseConfig/cloudFunctions.js';
import '../../database/encryption/model.js';
import { generateHash } from '../../database/encryption/utils/helpers.js';
import { getStore } from '../../store/helpers/initStore.js';
import { toastErrorAndReport } from '../toast/toast.js';

const getWaypointLocationHash = (waypoint) => {
    if (!waypoint.location)
        return undefined;
    const latLng = { lat: waypoint.location.lat, lng: waypoint.location.lng };
    return generateHash(latLng);
};
const getServiceRegionPolygon = async (serviceRegionID) => {
    const portalStore = usePortalStore();
    const cachedServiceRegionPolygon = portalStore.serviceRegionPolygons.get(serviceRegionID);
    if (cachedServiceRegionPolygon)
        return cachedServiceRegionPolygon;
    const serviceRegionPolygon = await portalStore.serviceRegionPolygons.fetch(serviceRegionID);
    return serviceRegionPolygon !== null && serviceRegionPolygon !== void 0 ? serviceRegionPolygon : undefined;
};
const getServiceRegionsByItineraryID = async (itineraryID, shiftID) => {
    var _a, _b, _c, _d;
    const portalStore = usePortalStore();
    const store = getStore();
    const waypoints = (_b = (_a = portalStore.itineraries.get(itineraryID)) === null || _a === void 0 ? void 0 : _a.computed.waypoints) !== null && _b !== void 0 ? _b : [];
    let serviceRegions = {};
    (await Promise.all(waypoints.map(async (waypoint) => {
        const hash = getWaypointLocationHash(waypoint);
        const cachedSR = hash ? portalStore.serviceRegionCache.get(hash) : undefined;
        if (cachedSR) {
            serviceRegions = {
                ...serviceRegions,
                ...cachedSR.serviceRegions,
            };
            await getServiceRegionPolygon(cachedSR.id);
            return cachedSR.serviceRegions;
        }
        const sr = await getServiceRegionFromLocation(waypoint.location, store.state.variables.variables);
        if (isNullish(sr) || isNullish(hash))
            return;
        portalStore.serviceRegionCache.set({
            id: hash,
            serviceRegions: {
                [sr.id]: sr,
            },
        });
        await getServiceRegionPolygon(sr.id);
        return sr;
    }))).filter(nullishFilter);
    portalStore.serviceRegionCache.set({
        id: itineraryID,
        serviceRegions,
    });
    if (shiftID) {
        portalStore.serviceRegionCache.set({
            id: shiftID,
            serviceRegions: {
                ...serviceRegions,
                ...((_d = (_c = portalStore.serviceRegionCache.get(shiftID)) === null || _c === void 0 ? void 0 : _c.serviceRegions) !== null && _d !== void 0 ? _d : {}),
            },
        });
    }
    return Object.values(serviceRegions);
};
const getServiceRegionIDsByItineraryID = async (itineraryID, shiftID) => {
    const serviceRegionNames = (await getServiceRegionsByItineraryID(itineraryID, shiftID)).map((sr) => sr.id);
    return deduplicateArray(serviceRegionNames);
};
const getServiceRegionByID = async (serviceRegionID, cacheKey) => {
    var _a, _b, _c;
    const store = getStore();
    const portalStore = usePortalStore();
    const cachedServiceRegion = cacheKey ? portalStore.serviceRegionCache.getRaw(cacheKey) : undefined;
    if (cachedServiceRegion) {
        await getServiceRegionPolygon(cachedServiceRegion.id);
        return cachedServiceRegion.serviceRegions[serviceRegionID];
    }
    const serviceRegion = (_a = await portalStore.serviceRegions.fetch(serviceRegionID)) !== null && _a !== void 0 ? _a : (_c = (_b = store.state.variables.variables) === null || _b === void 0 ? void 0 : _b.serviceRegions) === null || _c === void 0 ? void 0 : _c[serviceRegionID];
    if (!serviceRegion)
        return;
    await getServiceRegionPolygon(serviceRegion.id);
    if (cacheKey) {
        portalStore.serviceRegionCache.set({
            id: cacheKey,
            serviceRegions: {
                [serviceRegionID]: serviceRegion,
            },
        });
    }
    return serviceRegion;
};
const getServiceRegionIDsByShiftID = async (shiftID) => {
    const portalStore = usePortalStore();
    const shift = portalStore.shifts.get(shiftID);
    if (!shift)
        return [];
    const { itineraries, serviceRegions } = shift;
    const itineraryServiceRegionIDs = Object.keys(itineraries !== null && itineraries !== void 0 ? itineraries : {}).map((itineraryID) => getServiceRegionIDsByItineraryID(itineraryID, shiftID));
    const directServiceRegionIDs = Object.keys(serviceRegions !== null && serviceRegions !== void 0 ? serviceRegions : {}).map(async (serviceRegionID) => {
        var _a;
        /** This triggers a fetch for the service region polygon */
        const sr = await getServiceRegionByID(serviceRegionID, shiftID);
        return (_a = sr === null || sr === void 0 ? void 0 : sr.id) !== null && _a !== void 0 ? _a : serviceRegionID;
    });
    const serviceRegionIDs = await Promise.all([
        ...itineraryServiceRegionIDs,
        ...directServiceRegionIDs,
    ]);
    return deduplicateArray(serviceRegionIDs.flat().filter(nullishFilter));
};
/**
 * Returns `phoneNumber` if fax will be sent on waypoint completion, `undefined`
 * otherwise.
 *
 * Criteria for sending a fax:
 * - Drop-off waypoint has a `place` with a `faxNumber`
 * - Instructional requisition form is present on the procedure
 * - `labOrderID` is **not** present on the procedure
 * - Procedure is not canceled
 * - Procedure must be on a patient waypoint on the same itinerary
 */
const waypointRequiresFaxSending = (waypointID, procedureID) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    if (!waypointID || !procedureID)
        return;
    const store = getStore();
    const portalStore = usePortalStore();
    const savedItineraryWaypoint = (_b = (_a = store.getters)['itineraries/getWaypointByID']) === null || _b === void 0 ? void 0 : _b.call(_a, waypointID);
    const draftItineraryWaypoint = portalStore.waypoints.get(waypointID);
    const waypoint = savedItineraryWaypoint !== null && savedItineraryWaypoint !== void 0 ? savedItineraryWaypoint : draftItineraryWaypoint;
    if (!waypoint) {
        toastErrorAndReport(`Waypoint ${waypointID} not found`);
        return;
    }
    const { placeID } = waypoint;
    const faxNumbers = (_e = (_d = (_c = store.getters)['places/getPlaceByPlaceID']) === null || _d === void 0 ? void 0 : _d.call(_c, placeID)) === null || _e === void 0 ? void 0 : _e.faxNumbers;
    const procedure = (_g = (_f = store.getters)['procedures/getProcedureByID']) === null || _g === void 0 ? void 0 : _g.call(_f, procedureID);
    const reqForm = (_h = procedure === null || procedure === void 0 ? void 0 : procedure.documents) === null || _h === void 0 ? void 0 : _h.find((document) => document.type === ProcedureDocumentType.INSTRUCTIONAL_REQUISITION_FORM);
    if (!waypoint.itineraryID) {
        toastErrorAndReport(`Missing itineraryID on waypoint ${waypointID}`);
        return;
    }
    // Check if there is a matching patient waypoint action on the same itinerary.
    // Currently, this should always be the case, but if we add support for
    // drop-off on separate itineraries, that will change.
    const patientWaypointActions = (_k = (_j = store.getters)['itineraries/getPatientWaypointActionsOnItinerary']) === null || _k === void 0 ? void 0 : _k.call(_j, waypoint.itineraryID);
    const patientActionProcedureIDs = patientWaypointActions === null || patientWaypointActions === void 0 ? void 0 : patientWaypointActions.reduce((ids, waypointAction) => {
        var _a;
        const procedureIDs = Object.keys((_a = waypointAction.procedures) !== null && _a !== void 0 ? _a : {});
        return deduplicateArray([...ids, ...procedureIDs]);
    }, []);
    const matchingWaypointActionFound = patientActionProcedureIDs === null || patientActionProcedureIDs === void 0 ? void 0 : patientActionProcedureIDs.includes(procedureID);
    const criteriaMet = (!!faxNumbers
        && !!reqForm
        && !hasStatus(procedure, ProcedureStatus.CANCELED)
        && matchingWaypointActionFound);
    const faxNumbersFormatted = faxNumbers === null || faxNumbers === void 0 ? void 0 : faxNumbers.map(formatPhoneNumber).join(', ');
    return criteriaMet ? faxNumbersFormatted : undefined;
};

export { getServiceRegionIDsByItineraryID, getServiceRegionIDsByShiftID, getServiceRegionsByItineraryID, waypointRequiresFaxSending };
